import { defineStore } from 'pinia'
import { consola } from 'consola'
import { apiSupportChat } from '../api/apiSupportChat'
import {
  SUPPORT_WS_EVENTS,
  type SupportAttachment,
  type SupportMessage,
  type SupportMessageReactionEvent,
  type SupportMessageStatusEvent,
  type SupportTicket,
  type SupportTicketType,
} from './types'
import { useSupportSocket } from './useSupportSocket'
import { useStickerPacks } from './stickerPacks'
import {
  applyReactionEventToMessages,
  applyStatusEventToMessages,
  findNewMessagesAnchor,
  toReplyPreview,
  toggleOwnReaction,
  upsertTicketIn,
} from './dialogCore'

const logger = consola.withTag('support-chat')

/**
 * Стор клиентского чата поддержки: список обращений, активный диалог, live-события.
 * Отправка — REST (оффлайн-очередь и ретраи бесплатно), приём — socket.io.
 */
export const useSupportChatStore = defineStore('supportChat', () => {
  const tickets = ref<SupportTicket[]>([])
  const messages = ref<SupportMessage[]>([])
  const openedTicketId = ref<string | null>(null)
  const hasMore = ref(false)
  const nextCursor = ref<string | null>(null)
  const isLoading = ref(false)
  const isSending = ref(false)
  const isRating = ref(false)
  /**
   * Первое непрочитанное сообщение на момент ОТКРЫТИЯ чата — перед ним рисуется
   * планка «Новые сообщения». Считается до markRead и живёт, пока чат открыт:
   * иначе отметка о прочтении стирала бы место, где человек остановился.
   */
  const newMessagesAnchorId = ref<string | null>(null)
  /** Сообщения, пришедшие уже при открытом чате — для плашки «N новых ↓» */
  const liveUnseenIds = ref<string[]>([])
  /** Сообщение, на которое отвечаем (панель над полем ввода); null — обычная отправка */
  const replyTo = ref<SupportMessage | null>(null)

  let unsubscribers: Array<() => void> = []
  let optimisticSeq = 0

  /** Активное (не закрытое) обращение — по правилу «один активный тикет» */
  const activeTicket = computed(() =>
    tickets.value.find(t => t.status !== 'closed') ?? null,
  )
  const openedTicket = computed(() =>
    tickets.value.find(t => t.id === openedTicketId.value) ?? null,
  )
  const unreadTotal = computed(() =>
    tickets.value.reduce((sum, t) => sum + t.unreadByUser, 0),
  )

  function upsertTicket(ticket: SupportTicket): void {
    upsertTicketIn(tickets.value, ticket)
  }

  async function fetchTickets(): Promise<void> {
    try {
      isLoading.value = true
      tickets.value = await apiSupportChat.getMyTickets()
    } catch (error) {
      logger.error('Не удалось загрузить обращения:', error)
    } finally {
      isLoading.value = false
    }
  }

  async function openTicket(ticketId: string): Promise<void> {
    openedTicketId.value = ticketId
    messages.value = []
    nextCursor.value = null
    hasMore.value = false
    newMessagesAnchorId.value = null
    liveUnseenIds.value = []
    replyTo.value = null
    try {
      isLoading.value = true
      const page = await apiSupportChat.getMessages(ticketId)
      messages.value = page.messages
      hasMore.value = page.hasMore
      nextCursor.value = page.nextCursor

      // Якорь ставим ДО markRead — после него readAt проставлен у всех
      newMessagesAnchorId.value = findNewMessagesAnchor(page.messages, 'user')

      await markRead(ticketId)
    } catch (error) {
      logger.error('Не удалось загрузить сообщения:', error)
    } finally {
      isLoading.value = false
    }
  }

  /** Пользователь долистал до низа — плашка «N новых» больше не нужна */
  function clearLiveUnseen(): void {
    liveUnseenIds.value = []
  }

  async function loadOlder(): Promise<void> {
    if (!openedTicketId.value || !hasMore.value || !nextCursor.value) return
    const page = await apiSupportChat.getMessages(openedTicketId.value, nextCursor.value)
    messages.value = [...page.messages, ...messages.value]
    hasMore.value = page.hasMore
    nextCursor.value = page.nextCursor
  }

  async function createTicket(payload: {
    type: SupportTicketType
    subject?: string
    orderId?: string
    text?: string
    attachments?: SupportAttachment[]
  }): Promise<SupportTicket> {
    isSending.value = true
    try {
      const { ticket, message } = await apiSupportChat.createTicket(payload)
      upsertTicket(ticket)
      openedTicketId.value = ticket.id
      messages.value = [message]
      hasMore.value = false
      nextCursor.value = null
      return ticket
    } catch (error: any) {
      // 409 = активный тикет уже есть — открываем его вместо создания нового
      // (ofetch кладёт тело ответа в error.data / error.response._data)
      const activeId = error?.data?.activeTicketId ?? error?.response?._data?.activeTicketId
      if (activeId) {
        logger.info('Активное обращение уже есть, открываем его', { activeId })
        await fetchTickets()
        await openTicket(String(activeId))
        const existing = tickets.value.find(t => t.id === String(activeId))
        if (existing) return existing
      }
      throw error
    } finally {
      isSending.value = false
    }
  }

  async function sendMessage(payload: {
    text?: string
    attachments?: SupportAttachment[]
    orderId?: string
    stickerId?: string
  }): Promise<void> {
    const ticketId = openedTicketId.value
    if (!ticketId) return

    // Цитату снимаем сразу вместе с черновиком: панель ответа не должна висеть,
    // пока сообщение уже улетело
    const answered = replyTo.value
    replyTo.value = null

    // Оптимистичный пузырь с часиками: сообщение видно сразу, статус доедет с ответом
    const optimisticId = `pending-${++optimisticSeq}`
    const optimistic: SupportMessage = {
      id: optimisticId,
      ticketId,
      authorType: 'user',
      text: payload.text ?? null,
      attachments: payload.attachments ?? [],
      orderId: payload.orderId ?? null,
      replyTo: answered ? toReplyPreview(answered) : null,
      stickerId: payload.stickerId ?? null,
      reactions: [],
      deliveredAt: null,
      readAt: null,
      createdAt: new Date().toISOString(),
      pending: true,
    }
    messages.value.push(optimistic)

    isSending.value = true
    try {
      const message = await apiSupportChat.sendMessage(ticketId, {
        ...payload,
        // Оптимистичный id сервер не знает: отвечать можно только на сохранённое
        ...(answered && !answered.pending ? { replyToId: answered.id } : {}),
      })
      const index = messages.value.findIndex(m => m.id === optimisticId)
      if (index !== -1) messages.value[index] = message
      else if (!messages.value.some(m => m.id === message.id)) messages.value.push(message)
    } catch (error) {
      // Неотправленное не оставляем «висеть» в ленте — иначе гость решит, что дошло
      messages.value = messages.value.filter(m => m.id !== optimisticId)
      // Цитату возвращаем вместе с текстом, который вернёт экран
      replyTo.value = answered
      throw error
    } finally {
      isSending.value = false
    }
  }

  /** Стикер — самостоятельное сообщение (текст и вложения к нему не крепятся) */
  async function sendSticker(stickerId: string): Promise<void> {
    await sendMessage({ stickerId })
  }

  /**
   * Поставить/снять реакцию. Рисуем сразу, не дожидаясь сети: сервер всё равно
   * пришлёт полный набор реакций событием и перезапишет локальную догадку.
   */
  async function setReaction(messageId: string, emoji: string): Promise<void> {
    const ticketId = openedTicketId.value
    const message = messages.value.find(m => m.id === messageId)
    if (!ticketId || !message || message.pending) return

    const { next, rollback } = toggleOwnReaction(message, 'user', emoji)
    try {
      message.reactions = await apiSupportChat.setReaction(ticketId, messageId, next)
    } catch (error) {
      rollback()
      logger.warn('Не удалось поставить реакцию:', error)
      throw error
    }
  }

  function setReplyTo(message: SupportMessage | null): void {
    replyTo.value = message?.pending ? null : message
  }

  async function markRead(ticketId: string): Promise<void> {
    try {
      const ticket = await apiSupportChat.markRead(ticketId)
      upsertTicket(ticket)
    } catch (error) {
      logger.warn('Не удалось отметить прочитанным:', error)
    }
  }

  /** Подтвердить доставку сообщений админа (когда чат закрыт/свёрнут — просто «дошло») */
  async function markDelivered(ticketId: string): Promise<void> {
    try {
      await apiSupportChat.markDelivered(ticketId)
    } catch (error) {
      logger.debug('Не удалось подтвердить доставку:', error)
    }
  }

  async function rateTicket(payload: {
    rating: number
    resolved: boolean | null
    fast: boolean | null
    comment: string
  }): Promise<void> {
    if (!openedTicketId.value) return
    isRating.value = true
    try {
      const ticket = await apiSupportChat.rateTicket(openedTicketId.value, payload)
      upsertTicket(ticket)
    } finally {
      isRating.value = false
    }
  }

  /** Применить событие смены галочек к своим сообщениям */
  function applyStatusEvent(event: SupportMessageStatusEvent): void {
    applyStatusEventToMessages(messages.value, event, 'user')
  }

  /** Подключить сокет и подписаться на события (идемпотентно) */
  function startRealtime(): void {
    const socket = useSupportSocket()
    if (!socket.connect('user')) return

    stopRealtimeSubscriptions()
    unsubscribers = [
      socket.on<{ ticketId: string, message: SupportMessage, ticket?: SupportTicket }>(
        SUPPORT_WS_EVENTS.MESSAGE_NEW,
        (payload) => {
          if (payload.ticket) upsertTicket(payload.ticket)
          if (payload.ticketId === openedTicketId.value) {
            if (!messages.value.some(m => m.id === payload.message.id)) {
              messages.value.push(payload.message)
              // Планка «новые» ставится на первое сообщение пачки, пришедшей
              // при открытом чате: человек мог отвлечься и не заметить ответ
              if (!newMessagesAnchorId.value) newMessagesAnchorId.value = payload.message.id
              liveUnseenIds.value = [...liveUnseenIds.value, payload.message.id]
            }
            // Чат открыт — сразу помечаем прочитанным (прочтение включает доставку)
            void markRead(payload.ticketId)
          } else {
            // Чат закрыт, но приложение на связи — это «доставлено», не «прочитано»
            void markDelivered(payload.ticketId)
          }
        },
      ),
      socket.on<{ ticket: SupportTicket }>(SUPPORT_WS_EVENTS.TICKET_UPDATED, (payload) => {
        upsertTicket(payload.ticket)
      }),
      socket.on<SupportMessageStatusEvent>(SUPPORT_WS_EVENTS.MESSAGE_STATUS, applyStatusEvent),
      socket.on<SupportMessageReactionEvent>(SUPPORT_WS_EVENTS.MESSAGE_REACTION, (payload) => {
        applyReactionEventToMessages(messages.value, payload)
      }),
    ]
  }

  function stopRealtimeSubscriptions(): void {
    for (const unsubscribe of unsubscribers) unsubscribe()
    unsubscribers = []
  }

  function reset(): void {
    stopRealtimeSubscriptions()
    // Сокет аутентифицирован токеном ПРОШЛОГО пользователя и сам не умрёт:
    // без разрыва события чата продолжали бы приходить под старой личностью
    useSupportSocket().disconnect()
    // Пакеты стикеров привязаны к заведению прошлой сессии — в памяти им не место
    useStickerPacks().reset()
    tickets.value = []
    messages.value = []
    openedTicketId.value = null
    hasMore.value = false
    nextCursor.value = null
    newMessagesAnchorId.value = null
    liveUnseenIds.value = []
    replyTo.value = null
  }

  return {
    tickets, messages, openedTicketId, hasMore, isLoading, isSending, isRating,
    newMessagesAnchorId, liveUnseenIds, replyTo,
    activeTicket, openedTicket, unreadTotal,
    fetchTickets, openTicket, loadOlder, createTicket, sendMessage, sendSticker, markRead,
    markDelivered, rateTicket, startRealtime, reset, clearLiveUnseen, setReaction, setReplyTo,
  }
})
